import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Loader2 } from "lucide-react";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import DynamicTable from "../../../components/Table/DIndentTable2";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import { fetchIndentsAsync, fetchDropdownDataAsync, setFilteredIndents } from '../../../store/peindentSlice';
import { format } from "date-fns";

const LoadingSpinner = React.memo(() => (
  <div className="flex justify-center items-center p-4">
    <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
  </div>
));

const MemoizedActionBar = React.memo(ActionBar);
const MemoizedDynamicTable = React.memo(DynamicTable);

const PEIndent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { role, id: enteredBy } = useSelector((state) => state.auth);
  const { 
    indents, 
    filteredIndents, 
    dropdownData, 
    loading: { indents: isLoading, dropdown: dropdownLoading },
    error 
  } = useSelector((state) => state.peindent);
  
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Add ref to track if data has been fetched
  const hasFetchedData = useRef(false);

  // Memoize the fetch parameters to prevent unnecessary re-fetches
  const fetchParams = useMemo(() => ({ role, enteredBy }), [role, enteredBy]);

  // Memoize API endpoint
  const apiEndpoint = useMemo(() => 
    `${process.env.REACT_APP_API_BASE_URL}/indent/indent`, 
    []
  );
  
  useEffect(() => {
    if (!hasFetchedData.current) {
      dispatch(fetchIndentsAsync(fetchParams));
      dispatch(fetchDropdownDataAsync());
      hasFetchedData.current = true;
    }
  }, [dispatch, fetchParams]);

  // Remove this useEffect that logs on every filteredIndents change
  // This was causing unnecessary re-renders
  /* useEffect(() => {
    if (filteredIndents.length > 0) {
      console.log('First indent data:', filteredIndents[0]);
      console.log('Creation date from first indent:', filteredIndents[0].CreationDate);
    }
  }, [filteredIndents]); */

  // Memoize the formatDate function to prevent recreation on every render
  const formatDate = useCallback((dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    // If the date is already in dd/mm/yy format, return it as is
    if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateString)) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "N/A";
      }
      return format(date, "dd/MM/yy");
    } catch (error) {
      return "N/A";
    }
  }, []);

  const getEditFields = useMemo(() => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options: dropdownData?.projects?.map(project => ({
        value: project.id,
        label: project.name,
      })) || [],
      className: "w-full",
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options: dropdownData?.types?.map(type => ({
        value: type.id,
        label: type.name,
      })) || [],
      className: "w-full",
    },
  ], [dropdownData?.projects, dropdownData?.types]);

  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      dispatch(setFilteredIndents(indents));
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
    const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.Status,
        indent.ProjectName,
        indent.TypeName,
        indent.CreationDate?.toString()
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
    );
    });
    dispatch(setFilteredIndents(filtered));
  }, [indents, dispatch]);

  const handleDateFilter = useCallback((filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (!dateFrom || !dateTo) {
      dispatch(setFilteredIndents(indents));
      return;
    }

    const filtered = indents.filter((indent) => {
      const creationDate = new Date(indent.CreationDate);
      return creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo);
    });
    dispatch(setFilteredIndents(
      filtered.sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate))
    ));
  }, [indents, dispatch]);

  const handleRowClick = useCallback((row) => {
    if (row) {
      navigate(`/peindent/view`, { 
        state: { 
          row,
          message: `Viewing indent ${row.IndentID}`
        } 
      });
    }
  }, [navigate]);

  // Memoize the refresh handler
  const handleRefresh = useCallback(() => {
    dispatch(fetchIndentsAsync(fetchParams));
  }, [dispatch, fetchParams]);

  // Memoize table columns to prevent recreation
  const tableColumns = useMemo(() => [
    { key: "IndentID", label: "ID" },
    { 
      key: "CreationDate", 
      label: "Creation Date",
      render: (row) => formatDate(row.CreationDate)
    },
    { key: "ProjectName", label: "Project" },
    { key: "TypeName", label: "Type" },
    { key: "Status", label: "Status" },
    { key: "ItemCount", label: "Items" },
  ], [formatDate]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <p className="text-red-500 text-center">Error: {error}</p>
      </div>
    );
  }

  if (isLoading || dropdownLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <div className="flex-grow p-2 sm:p-4">
        <div className="mb-4 text-center">
          <h3 className="text-xl font-semibold">PE Table</h3>
        </div>

        <div className="flex flex-col gap-4">
          {/* ActionBar - Always at the top */}
          <div className="bg-white p-4 rounded-lg shadow-sm">
            {dropdownData?.users?.length > 0 && dropdownData?.projects?.length > 0 && (
              <MemoizedActionBar
                title="Indent"
                fields={getEditFields}
                apiEndpoint={apiEndpoint}
                data={filteredIndents}
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                onRefresh={handleRefresh}
                onSearch={handleSearch}
                onFilter={handleDateFilter}
                rowKey="IndentID"
                userRole={role}
                addLink="/addIndent"
                editRoute="/PETable"
                className="w-full"
                searchQuery={searchQuery}
              />
            )}
          </div>

          {/* Table - Below ActionBar */}
          <div className="bg-white rounded-lg shadow-sm p-2 sm:p-4">
            <div className="overflow-x-auto">
              <MemoizedDynamicTable
                columns={tableColumns}
                data={filteredIndents}
                selectedRow={selectedRow}
                onRowSelect={setSelectedRow}
                onDoubleClick={handleRowClick}
                rowKey="IndentID"
                userIdKey="UserID"
                highlightColor="bg-blue-100"
                noDataMessage="No indents found."
                userRole={role}
                mobileView={true}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default React.memo(PEIndent);